import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { TypedMemo } from '../../common/types';
import { SMLThemeContext } from './SMLThemeContext';
import {
  applySMLThemeToDocument,
  getInitialSMLThemePreference,
  getSystemSMLTheme,
  resolveSMLTheme,
  SMLTheme,
  SMLThemePreference,
  storeSMLThemePreference,
  subscribeToSystemSMLTheme,
  parseSMLThemePreference,
  DEFAULT_SML_THEME_PREFERENCE,
  SML_THEME_STORAGE_KEY,
} from './smlTheme';

const THEMING_CLASS = 'theming';
const THEMING_DURATION_MS = 360;

function startCrossFade(): void {
  if (typeof document === 'undefined') {
    return;
  }
  const root = document.documentElement;
  root.classList.add(THEMING_CLASS);
  window.setTimeout(() => root.classList.remove(THEMING_CLASS), THEMING_DURATION_MS);
}

const SMLThemeProviderComponent: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [preference, setPreferenceState] = useState<SMLThemePreference>(() => getInitialSMLThemePreference());
  const [theme, setThemeState] = useState<SMLTheme>(() => resolveSMLTheme(preference));

  useLayoutEffect(() => {
    applySMLThemeToDocument(theme);
  }, [theme]);

  // The `system` choice follows the device color scheme, also while the page is open. A light or
  // dark choice stops it.
  const followsSystem = preference === 'system';
  useEffect(() => {
    if (!followsSystem) {
      return undefined;
    }
    // The scheme can change between the first render and this effect.
    setThemeState(getSystemSMLTheme());
    return subscribeToSystemSMLTheme(setThemeState);
  }, [followsSystem]);

  // A choice made in another tab (or on music24.com, same origin) applies here too.
  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (event.key !== SML_THEME_STORAGE_KEY) {
        return;
      }
      const next = parseSMLThemePreference(event.newValue) ?? DEFAULT_SML_THEME_PREFERENCE;
      setPreferenceState(next);
      setThemeState(resolveSMLTheme(next));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setPreference = useCallback((next: SMLThemePreference) => {
    startCrossFade();
    storeSMLThemePreference(next);
    setPreferenceState(next);
    setThemeState(resolveSMLTheme(next));
  }, []);

  const setTheme = useCallback((next: SMLTheme) => setPreference(next), [setPreference]);

  const toggleTheme = useCallback(() => {
    setPreference(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setPreference]);

  const value = useMemo(
    () => ({ preference, theme, setPreference, setTheme, toggleTheme }),
    [preference, theme, setPreference, setTheme, toggleTheme]
  );

  return <SMLThemeContext.Provider value={value}>{children}</SMLThemeContext.Provider>;
};

export const SMLThemeProvider = TypedMemo(SMLThemeProviderComponent);
